'use client';

import { useCallback, useState } from 'react';
import { useSession } from 'next-auth/react';
import { switchActiveOrg } from '@/apis/crossOrgCombining';
import { useAuthStore } from '@/state/authState';
import useOrganizationStore from '@/state/orgState';
import useUserStore from '@/state/userState';
import useUserContextStore from '@/contexts/UserContext';
import { useToast } from '@/hooks/useToast';

/**
 * Become a sibling org's own native identity (its own admin/management
 * context). Distinct from linking for combining: this mints a fresh JWT
 * and re-authenticates the session as the target org's row.
 *
 * Adopts the new JWT via `update()`, which the NextAuth `jwt` callback
 * special-cases (trigger === 'update' + a `backendJwt` payload) to store
 * it directly instead of re-deriving one from the OAuth provider token --
 * re-deriving would always resolve back to the same identity row and
 * silently discard the switch.
 *
 * Every org-scoped Zustand store is cleared and the app is hard-navigated
 * (not router.push) to /home, same as the existing 401 handler in
 * httpAdapter.ts: several stores key off module-level "already
 * initialized" flags that a client-side route change would not re-run.
 */
export function useSwitchActiveOrg() {
  const { update } = useSession();
  const toast = useToast();
  const [switching, setSwitching] = useState<string | null>(null);

  const switchTo = useCallback(
    async (targetOrgUUID: string, targetOrgName: string) => {
      setSwitching(targetOrgUUID);
      const result = await switchActiveOrg(targetOrgUUID);
      if (result.Error || !result.JWT || !result.ExpiresIn) {
        toast({
          title: `Couldn't switch to ${targetOrgName}`,
          description: 'Please try again in a moment.',
          status: 'error',
          duration: 5000,
        });
        setSwitching(null);
        return;
      }

      await update({
        backendJwt: result.JWT,
        backendJwtExpiresAt: Date.now() + result.ExpiresIn * 1000,
      });

      useAuthStore.getState().clearAllAuth();
      useOrganizationStore.getState().clearOrganization();
      useUserContextStore.getState().reset();
      useUserStore.getState().clearUser();

      window.location.assign('/home');
    },
    [update, toast]
  );

  return { switchTo, switching };
}
